import React, { KeyboardEvent, useEffect, useRef, useState } from "react";

import { ChevronDownIcon } from "@heroicons/react/20/solid";
import classNames from "classnames";
import { useFormikContext } from "formik";

import { TitleTypeEnum } from "../../../../enums/template";
import { Template } from "../../../../models";
import TemplateContainer from "../common/TemplateContainer";

export interface Option {
  type: TitleTypeEnum;
  description: string;
}

const titleTypes: Option[] = [
  {
    type: TitleTypeEnum.NONE,
    description: "",
  },
  {
    type: TitleTypeEnum.STANDARD,
    description: "SEO-friendly blog title that balances clarity and curiosity.",
  },
  {
    type: TitleTypeEnum.SHORT_SUMMARY,
    description:
      "Clear, short, punchy headline that captures the main takeaway.",
  },
  {
    type: TitleTypeEnum.LONG_SUMMARY,
    description:
      "Descriptive and informative title that captures the full scope of the article.",
  },
  {
    type: TitleTypeEnum.NEWS,
    description:
      "Direct or action-oriented headline suitable for a news-style article.",
  },
  {
    type: TitleTypeEnum.LISTICLE,
    description:
      "Numbered title that clearly states what the reader will learn.",
  },
  {
    type: TitleTypeEnum.TUTORIAL,
    description:
      'Helpful title that starts with "How to" or similar instruction-based language.',
  },
  {
    type: TitleTypeEnum.OPINION,
    description:
      "Provocative or thoughtful opinion title that reflects a clear point of view.",
  },
  {
    type: TitleTypeEnum.INTERVIEW,
    description: "Title that highlights the subject and value of an interview.",
  },
  {
    type: TitleTypeEnum.ACTION_STEPS,
    description:
      "Title that highlights actionable steps or tasks readers can follow.",
  },
  {
    type: TitleTypeEnum.PERSONAL_STORIES,
    description: "Relatable and engaging title based on a personal experience.",
  },
  {
    type: TitleTypeEnum.PRODUCT_REVIEW,
    description: "Clear, honest, and informative title for a product review.",
  },
  {
    type: TitleTypeEnum.GUIDE,
    description:
      "Comprehensive title for a step-by-step, all-in-one, or ultimate guide.",
  },
  {
    type: TitleTypeEnum.CASE_STUDY,
    description:
      "Reflects the problem-solving nature or the results and insights of a case study.",
  },
  {
    type: TitleTypeEnum.SMART_BREVITY,
    description:
      "Informative, punchy title that delivers value fast—think Axios style.",
  },
  {
    type: TitleTypeEnum.QUESTION,
    description:
      "Compelling title in the form of a question to spark curiosity.",
  },
  {
    type: TitleTypeEnum.PROBLEM_SOLUTION,
    description: "Title that frames a common problem and hints at a solution.",
  },
  {
    type: TitleTypeEnum.QUOTE_STORY,
    description:
      "Title inspired by a standout quote or message from the article.",
  },
  {
    type: TitleTypeEnum.EXPERT,
    description:
      "Title that highlights expert advice, analysis, or predictions.",
  },
  {
    type: TitleTypeEnum.TOP_ITEMS,
    description: "Title that features a list of top items, people, or tools.",
  },
];

const titleTypesMap = {
  [TitleTypeEnum.NONE]: "-",
  [TitleTypeEnum.STANDARD]: "Standard",
  [TitleTypeEnum.SHORT_SUMMARY]: "Short Summary",
  [TitleTypeEnum.LONG_SUMMARY]: "Long Summary",
  [TitleTypeEnum.NEWS]: "News",
  [TitleTypeEnum.LISTICLE]: "Listicle",
  [TitleTypeEnum.TUTORIAL]: "Tutorial",
  [TitleTypeEnum.OPINION]: "Opinion",
  [TitleTypeEnum.INTERVIEW]: "Interview",
  [TitleTypeEnum.ACTION_STEPS]: "Action Steps",
  [TitleTypeEnum.PERSONAL_STORIES]: "Personal Stories",
  [TitleTypeEnum.PRODUCT_REVIEW]: "Product Review",
  [TitleTypeEnum.GUIDE]: "Guide",
  [TitleTypeEnum.CASE_STUDY]: "Case Study",
  [TitleTypeEnum.SMART_BREVITY]: "Smart Brevity",
  [TitleTypeEnum.QUESTION]: "Question",
  [TitleTypeEnum.PROBLEM_SOLUTION]: "Problem Solution",
  [TitleTypeEnum.QUOTE_STORY]: "Quote Story",
  [TitleTypeEnum.EXPERT]: "Expert",
  [TitleTypeEnum.TOP_ITEMS]: "Top Items",
};

const TitleType = () => {
  const { values, setFieldValue } = useFormikContext<Partial<Template>>();
  const selectedTitleType: TitleTypeEnum =
    values?.titleTypeId ?? TitleTypeEnum.NONE;

  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dropdownButtonRef = useRef<HTMLButtonElement>(null);
  const optionRefs = useRef<HTMLTableRowElement[]>([]);

  // Memoized handler to avoid recreating on every render
  const handleSelect = (type: TitleTypeEnum) => {
    setIsOpen(false);
    setFieldValue("titleTypeId", type === -1 ? null : type);
    // Focus back on the dropdown button after selection
    setTimeout(() => dropdownButtonRef.current?.focus(), 0);
  };

  // Initialize refs array for option elements
  useEffect(() => {
    optionRefs.current = optionRefs.current.slice(0, titleTypes.length);
  }, []);

  // Handle outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Check if dropdownRef is defined and if the click was outside
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setActiveIndex(-1);
      }
    };

    // Add event listener if dropdown is open
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    // Cleanup event listener
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Reset active index when dropdown opens/closes
  useEffect(() => {
    if (!isOpen) {
      setActiveIndex(-1);
    } else {
      // Set initial active item to the currently selected type when dropdown opens
      const currentIndex = titleTypes.findIndex(
        (item) => item.type === selectedTitleType
      );
      setActiveIndex(currentIndex >= 0 ? currentIndex : 0);
    }
  }, [isOpen, selectedTitleType]);

  // Focus on the active option when activeIndex changes
  useEffect(() => {
    if (isOpen && activeIndex >= 0 && activeIndex < optionRefs.current.length) {
      optionRefs.current[activeIndex]?.focus();
    }
  }, [activeIndex, isOpen]);

  // Handle keyboard navigation
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!isOpen) {
      // Open dropdown with down arrow or space or enter
      if (
        event.key === "ArrowDown" ||
        event.key === " " ||
        event.key === "Enter"
      ) {
        event.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    switch (event.key) {
      case "Escape":
        // Close dropdown
        event.preventDefault();
        setIsOpen(false);
        dropdownButtonRef.current?.focus();
        break;
      case "ArrowDown":
        // Navigate to next option
        event.preventDefault();
        setActiveIndex((prevIndex) => {
          const nextIndex = (prevIndex + 1) % titleTypes.length;
          return nextIndex;
        });
        break;
      case "ArrowUp":
        // Navigate to previous option
        event.preventDefault();
        setActiveIndex((prevIndex) => {
          const nextIndex =
            prevIndex <= 0 ? titleTypes.length - 1 : prevIndex - 1;
          return nextIndex;
        });
        break;
      case "Home":
        // Go to first option
        event.preventDefault();
        setActiveIndex(0);
        break;
      case "End":
        // Go to last option
        event.preventDefault();
        setActiveIndex(titleTypes.length - 1);
        break;
      case "Enter":
      case " ":
        // Select current option
        event.preventDefault();
        if (activeIndex >= 0) {
          handleSelect(titleTypes[activeIndex].type);
        }
        break;
      case "Tab":
        // Close dropdown when tabbing out
        setIsOpen(false);
        break;
    }
  };

  return (
    <TemplateContainer isSuperUserFeature>
      <div>
        {/* Label and Tooltip */}
        <div className="mb-0.5 flex items-center">
          <label
            htmlFor="titleTypeButton"
            className="mb-1 flex items-center gap-2 uppercase text-xs tracking-wide font-medium leading-6 text-gray-900"
          >
            Title Style Override
            {/* <InfoTooltip
            id="titleType"
            tooltip="Each article type uses distinct structures, tones, and objectives to enhance readability and achieve engagement."
          /> */}
          </label>
        </div>

        <div
          className="relative"
          ref={dropdownRef}
          onKeyDown={handleKeyDown}
        >
          {/* Dropdown trigger */}
          <button
            id="titleTypeButton"
            ref={dropdownButtonRef}
            type="button"
            onClick={() => setIsOpen(!isOpen)}
            aria-haspopup="listbox"
            aria-expanded={isOpen}
            aria-labelledby="titleTypeLabel"
            className={classNames(
              "w-full flex items-center justify-between rounded-md px-2.5 py-2 bg-white text-sm text-left",
              "border-0 ring-1 ring-gray-300 hover:ring-gray-400 focus:ring-primary focus:outline-none",
              { "ring-primary": isOpen }
            )}
          >
            <span id="articleTypeLabel">
              {titleTypesMap[selectedTitleType] ?? "-"}
            </span>
            <div className="flex">
              <ChevronDownIcon
                className={`h-5 w-5 text-gray-400 transition-transform ${isOpen ? "transform rotate-180" : ""}`}
                aria-hidden="true"
              />
            </div>
          </button>

          {/* Dropdown menu */}
          {isOpen && (
            <div
              className="absolute z-10 w-full mt-1 bg-white shadow-lg rounded-md border border-gray-200 overflow-hidden"
              role="listbox"
              aria-labelledby="articleTypeLabel"
              tabIndex={-1}
            >
              <div className="max-h-72 overflow-y-auto">
                <table className="w-full text-sm text-left">
                  <thead className="text-xs text-gray-700 uppercase bg-gray-100">
                    <tr>
                      <th className="px-4 py-3">Type</th>
                      <th className="px-4 py-3">Description</th>
                      {/* <th className="px-4 py-3">Approx word count</th> */}
                    </tr>
                  </thead>
                  <tbody>
                    {titleTypes.map(({ type, description }, index) => (
                      <tr
                        key={type}
                        ref={(el) => {
                          if (el) optionRefs.current[index] = el;
                        }}
                        onClick={() => handleSelect(type)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleSelect(type);
                          }
                        }}
                        role="option"
                        aria-selected={selectedTitleType === type}
                        tabIndex={activeIndex === index ? 0 : -1}
                        className={classNames(
                          "border-b cursor-pointer outline-none",
                          {
                            "bg-gray-200 hover:bg-gray-200":
                              selectedTitleType === type ||
                              activeIndex === index,
                          },
                          {
                            "hover:bg-gray-50":
                              selectedTitleType !== type &&
                              activeIndex !== index,
                          }
                        )}
                      >
                        <td className="px-4 py-3 font-medium">
                          {titleTypesMap[type]}
                        </td>
                        <td className="px-4 py-3 text-gray-600">
                          {description}
                        </td>
                        {/* <td className="px-4 py-3 text-gray-600">
                          {targetWordCount}
                        </td> */}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </TemplateContainer>
  );
};

export default TitleType;
